import { Modal } from "react-bootstrap";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import UserIcon from "../../assets/img/user-icon.svg";
import IncomeSourceCard from "./IncomeSourceCard";
import useTranslator from "../../hooks/useTranslator";
import { convertCurrencyFormat, haveValue } from "../../helper/common";
import { useEffect, useMemo, useState } from "react";

const NON_INCOME_SOURCE_KEYS = [
  "name",
  "_id",
  "forensic_status",
  "step",
  "user_email",
  "profile_completed",
  "isForensicExists",
];
const INCOME_MODE_SOURCE_PREFIX = "__income_mode:";
const DEFAULT_INCOME_MODE = "avg";

const isIncomeModeSource = (source) =>
  typeof source === "string" && source.startsWith(INCOME_MODE_SOURCE_PREFIX);

const getSelectionArray = (selection) => {
  if (!haveValue(selection)) return [];

  return Array.isArray(selection) ? selection : [selection];
};

const getSelectionMode = (selection, fallbackMode = DEFAULT_INCOME_MODE) => {
  const modeSource = getSelectionArray(selection).find(isIncomeModeSource);
  const mode = modeSource?.replace(INCOME_MODE_SOURCE_PREFIX, "");

  return mode === "sum" || mode === "avg" ? mode : fallbackMode;
};

const getSelectionSources = (selection) =>
  getSelectionArray(selection).filter((source) => !isIncomeModeSource(source));

const buildPersistedSelection = (selection, mode) => {
  const sources = getSelectionSources(selection);

  return mode === "sum" && sources.length > 1
    ? [...sources, `${INCOME_MODE_SOURCE_PREFIX}${mode}`]
    : sources;
};

const getMonthlySourceAmount = (source, amount) => {
  const numericAmount = Number(amount);

  if (!Number.isFinite(numericAmount) || numericAmount <= 0) return 0;

  if (source?.startsWith("payslip:") || source === "declaredIncome") {
    return Math.round(numericAmount);
  }

  return Math.round(numericAmount / 12);
};

const getIncomeSourceKeys = (sources = {}) =>
  Object.keys(sources).filter((source) => {
    if (NON_INCOME_SOURCE_KEYS.includes(source)) return false;

    const value = sources[source];
    if (
      (source === "itr" || source === "bank") &&
      (value <= 0 || isNaN(value))
    ) {
      return false;
    }

    return true;
  });

const UpdatedIncomeSourceModal = ({
  show,
  onHide,
  updateSourceList,
  handleIncomeChange,
  loadFullInfo,
}) => {
  const { _t } = useTranslator();
  const params = new URLSearchParams(window.location.search);
  const guarantor = params.get("guarantor");

  const [selectedSource, setSelectedSources] = useState({});
  const [originalSourceMap, setOriginalSourceMap] = useState({});
  const [originalModeMap, setOriginalModeMap] = useState({});
  const [incomeModeByUser, setIncomeModeByUser] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!updateSourceList || !updateSourceList.length) return;

    const sourceMap = updateSourceList.reduce((acc, item) => {
      acc[item._id] = getSelectionSources(item.current_selected);

      return acc;
    }, {});
    const modeMap = updateSourceList.reduce((acc, item) => {
      acc[item._id] = getSelectionMode(
        item.current_selected,
        item.current_income_mode,
      );

      return acc;
    }, {});

    setSelectedSources(sourceMap);
    setOriginalSourceMap(sourceMap);
    setOriginalModeMap(modeMap);
    setIncomeModeByUser(modeMap);
  }, [updateSourceList]);

  const toggleSelectedSource = (userId, newSource) => {
    setSelectedSources((prev) => {
      const current = prev[userId] || [];

      return {
        ...prev,
        [userId]: current.includes(newSource)
          ? current.filter((s) => s !== newSource)
          : [...current, newSource],
      };
    });
  };

  const hasChanges = useMemo(() => {
    return updateSourceList.some((user) => {
      const original = originalSourceMap[user._id] || [];
      const updated = selectedSource[user._id] || [];
      const originalMode = originalModeMap[user._id] || DEFAULT_INCOME_MODE;
      const updatedMode = incomeModeByUser[user._id] || DEFAULT_INCOME_MODE;

      return (
        JSON.stringify(original) !== JSON.stringify(updated) ||
        originalMode !== updatedMode
      );
    });
  }, [
    incomeModeByUser,
    originalModeMap,
    selectedSource,
    originalSourceMap,
    updateSourceList,
  ]);

  const tenantIncomeByUser = useMemo(() => {
    return updateSourceList.reduce((acc, userObj) => {
      const selectedSources = selectedSource[userObj._id] || [];
      const mode = incomeModeByUser[userObj._id] || "avg";
      const selectedAmounts = selectedSources.map((source) =>
        getMonthlySourceAmount(source, userObj?.all_sources?.[source]),
      );
      const sum = selectedAmounts.reduce((total, amount) => total + amount, 0);
      const amount =
        mode === "avg" && selectedAmounts.length
          ? Math.round(sum / selectedAmounts.length)
          : sum;

      acc[userObj._id] = {
        amount,
        mode,
        selectedCount: selectedAmounts.length,
      };

      return acc;
    }, {});
  }, [incomeModeByUser, selectedSource, updateSourceList]);

  const combinedIncome = useMemo(() => {
    return Object.values(tenantIncomeByUser).reduce(
      (total, tenantIncome) => total + tenantIncome.amount,
      0,
    );
  }, [tenantIncomeByUser]);

  const hasEmptySelection = useMemo(() => {
    return updateSourceList.some((user) => {
      return (selectedSource[user._id] || []).length === 0;
    });
  }, [selectedSource, updateSourceList]);

  const updateTenantIncomeMode = (userId, mode) => {
    setIncomeModeByUser((prev) => ({
      ...prev,
      [userId]: mode,
    }));
  };

  const initiateSourceChange = async () => {
    if (isSaving || hasEmptySelection) return;

    const changedUsers = updateSourceList.filter((user) => {
      const original = originalSourceMap[user._id] || [];
      const updated = selectedSource[user._id] || [];
      const originalMode = originalModeMap[user._id] || DEFAULT_INCOME_MODE;
      const updatedMode = incomeModeByUser[user._id] || DEFAULT_INCOME_MODE;

      return (
        JSON.stringify(original) !== JSON.stringify(updated) ||
        originalMode !== updatedMode
      );
    });

    if (!changedUsers.length) {
      onHide();
      return;
    }

    try {
      setIsSaving(true);

      const requests = changedUsers.map((user) =>
        handleIncomeChange(
          buildPersistedSelection(
            selectedSource[user._id],
            incomeModeByUser[user._id] || DEFAULT_INCOME_MODE,
          ),
          haveValue(guarantor) ? true : user.type === "guarantor",
          haveValue(guarantor) ? guarantor : user._id,
        ),
      );

      await Promise.all(requests);

      loadFullInfo();

      setOriginalSourceMap(selectedSource);
      setOriginalModeMap(incomeModeByUser);

      onHide();
    } catch (error) {
      console.error("Income source update failed:", error);
      setIsSaving(false);
    }
  };

  const getTenantIncomeMeta = (tenantIncome) => {
    if (tenantIncome.selectedCount === 1) {
      return `1 ${_t("source")}`;
    }

    if (tenantIncome.mode === "sum") {
      return `${_t("sumOf")} ${tenantIncome.selectedCount}`;
    }

    return `${_t("avgOf")} ${tenantIncome.selectedCount}`;
  };

  const getInitials = (name = "") =>
    name
        .trim()
        .split(/\s+/)
        .map(word => word[0]?.toUpperCase() ?? "")
        .slice(0, 2)
        .join("");

  return (
    <Modal
      show={show}
      className="metermodal update-income-source-modal modal-xl modalWidth_set updateSourceModal"
      centered
      onHide={onHide}
    >
      <div className="update-income-source-modal-padding">
        <h4 className="custom-modal-heading">{_t("updateIncomeSource")}</h4>
        <p>{_t("updateIncomeSourceModelDescription")}</p>
        <ClearOutlinedIcon
          className="close-button-modal"
          onClick={onHide}
          style={{ cursor: "pointer" }}
        />
      </div>
      {updateSourceList.map((userObj) => {
        const tenantIncome = tenantIncomeByUser[userObj._id] || {
          amount: 0,
          mode: "avg",
          selectedCount: 0,
        };

        return (
          <div
            key={userObj._id}
            className={
              userObj._id !== updateSourceList[updateSourceList.length - 1]._id
                ? "multi-user-wrapper update-income-source-modal-padding"
                : "update-income-source-modal-padding mb-4"
            }
          >
            {haveValue(userObj?.all_sources) && (
              <>
                <div className="update-income-source-user-row">
                  <div className="earner-info update-source-info user-info">
                    <div className="earner-thumbnail-wrapper">
                      {getInitials(userObj?.all_sources?.name)}
                    </div>
                    <h6 className="earner-name">
                      {userObj?.all_sources?.name}
                    </h6>
                    <div className="update-source-income-divider" />
                    <h6 className="update-source-income-value">
                      {convertCurrencyFormat(tenantIncome.amount)} €
                    </h6>
                    <span className="update-source-income-meta">
                      {getTenantIncomeMeta(tenantIncome)}
                    </span>
                  </div>
                  {tenantIncome.selectedCount > 1 && (
                    <div className="income-source-mode-toggle">
                      <button
                        type="button"
                        className={tenantIncome.mode === "avg" ? "active" : ""}
                        onClick={() =>
                          updateTenantIncomeMode(userObj._id, "avg")
                        }
                      >
                        {_t("avg")}
                      </button>
                      <button
                        type="button"
                        className={tenantIncome.mode === "sum" ? "active" : ""}
                        onClick={() =>
                          updateTenantIncomeMode(userObj._id, "sum")
                        }
                      >
                        {_t("sum")}
                      </button>
                    </div>
                  )}
                </div>
                <h4 className="custom-modal-heading sourceHeading">
                  {_t("sources")}
                </h4>
              </>
            )}
            <div className="row income-source-card-main">
              {haveValue(userObj?.all_sources) ? (
                getIncomeSourceKeys(userObj.all_sources).map((data) => {
                  const value = userObj.all_sources[data];

                  return (
                    <div className="col-md-6 income-source-card" key={data}>
                      <IncomeSourceCard
                        userId={userObj?._id}
                        amount={value}
                        type={data}
                        selectedSource={selectedSource?.[userObj?._id]}
                        changeSelectedSource={toggleSelectedSource}
                      />
                    </div>
                  );
                })
              ) : (
                <div className="text-center my-2">
                  {_t(
                    "incomeSourceNotFound",
                    "Text used if user doesn't have any income source",
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div className="update-income-source-footer">
        <div className="update-income-source-total">
          <div>{_t("combinedMonthlyIncome")}</div>
          <strong>{convertCurrencyFormat(combinedIncome)} €</strong>
        </div>
        <button
          className="btnNext save-changes-button"
          disabled={!hasChanges || hasEmptySelection || isSaving}
          onClick={initiateSourceChange}
        >
          {isSaving ? `${_t("saving")}...` : _t("apply")}
        </button>
      </div>
    </Modal>
  );
};

export default UpdatedIncomeSourceModal;
